import {
  type MediaProvider,
  AhaEditorUtils,
  tools,
  type slate,
} from '@aha-app/aha-editor-v2';

import $ from 'jquery';
// @ts-expect-error fileuploader is not typed
import qq from 'javascripts/fileuploader';
import { bringModalToFront } from 'javascripts/util/modal_priority';
import { showAlertMessage } from 'javascripts/messages';

interface MediaChooserParams {
  baseURL?: string;
  attachmentPath?: string;
  getEnclosingElement: () => HTMLElement | undefined;
  csrfToken?: string;
  attachmentBaseURL?: string;
  hasSharedPath: boolean;
  getSharingToken?: () => string;
  canAddAttachments?: boolean;
}

interface AttachmentsModalAttachment {
  file: {
    ext: string;
    name: string;
    type: {
      url: string;
    };
    url: string;
  };
  provider: string;
  uid: number;
}

interface AttachmentsModalCallbackResponse {
  attachmentExt: string;
  attachmentId: string;
  attachmentThumb: string;
  attachmentTitle: string;
  attachmentUrl: string;
  success: boolean;
}

class AhaMediaProvider implements MediaProvider {
  type: 'media' = 'media';

  baseURL: string;
  attachmentPath?: string;
  _enclosingElement: JQuery<HTMLElement>;
  attachmentsModal: any;
  getEnclosingElement: () => HTMLElement | undefined;
  csrfToken?: string;
  attachmentBaseURL?: string;
  hasSharedPath: boolean;
  getSharingToken: (() => string) | undefined;
  canAddAttachments: boolean;

  constructor({
    baseURL = window.location.origin,
    attachmentPath,
    getEnclosingElement,
    csrfToken,
    attachmentBaseURL,
    hasSharedPath,
    getSharingToken,
    canAddAttachments = true,
  }: MediaChooserParams) {
    this.baseURL = baseURL;
    this.attachmentPath = attachmentPath;
    this.getEnclosingElement = getEnclosingElement;
    this.csrfToken = csrfToken;
    this.attachmentBaseURL = attachmentBaseURL;
    this.hasSharedPath = hasSharedPath;
    this.getSharingToken = getSharingToken;
    this.canAddAttachments = canAddAttachments;
  }

  get $enclosingElement(): JQuery<HTMLElement> {
    if (this._enclosingElement) return this._enclosingElement;

    this._enclosingElement = $(this.getEnclosingElement()!);
    return this._enclosingElement;
  }

  onChooseMedia(editor: slate.Editor) {
    if (!this.canAddAttachments) return;

    this.chooseMedia({
      onMediaChosen: ({
        url,
        fileExt,
        mimeType,
        name,
      }: {
        url: string;
        fileExt: string;
        mimeType?: string;
        name?: string;
      }) => {
        void tools.ToolInsertMedia.insertMediaFromUrl(editor, {
          url,
          fileExtension: fileExt,
          mimeType,
          name,
        });
      },
      onChooserDismissed: () => {
        AhaEditorUtils.Editor.focus(editor);
      },
    });
  }

  onViewImage(src: string) {
    const getSharingToken = this.getSharingToken;
    const baseURL = this.attachmentBaseURL;
    const attachmentId = src
      ? src.match(new RegExp(`/attachments/(?<id>\\d+)/token`))?.groups?.id
      : null;

    if (!attachmentId) return;

    const headers: JQuery.AjaxSettings['headers'] = {
      'X-Requested-With': 'XMLHttpRequest',
    };

    if (getSharingToken) {
      headers.Authorization = `Bearer ${getSharingToken()}`;
    }

    // Let the server handle displaying the appropriate modal for the
    // attachment.
    $.ajax({
      url: `${baseURL || ''}/attachments/${attachmentId}/editor_modal`,
      headers,
      success(responseText) {
        if (getSharingToken) {
          const script = document.createElement('script');
          script.text = responseText;

          const head = document.head;
          if (head) {
            head.appendChild(script).parentNode?.removeChild(script);
          }
        }
      },
    });

    // It's possible that we're looking at an image embedded in a text
    // annotation comment in fullscreen mode. In order to view the attachment
    // modal, we need to bring it to the top of the z-index stack because
    // fullscreen editors are normally above modals.
    bringModalToFront('#attachment-modal-window, #aha-editor-popup-container');
  }

  onUploadFile(file: File): Promise<string | null> {
    if (!this.canAddAttachments) return Promise.resolve(null);

    let getAuthorization = this.getSharingToken;
    if (this.hasSharedPath && window.sharingJWT) {
      getAuthorization = () => window.sharingJWT!;
    }

    const csrfToken =
      this.csrfToken || $('meta[name=csrf-token]').attr('content');

    const customHeaders: JQuery.AjaxSettings['headers'] = {
      'X-CSRF-Token': csrfToken,
    };

    const authorization = getAuthorization && getAuthorization();
    if (authorization) {
      customHeaders.Authorization = `Bearer ${authorization}`;
    }

    return new Promise((resolve, reject) => {
      const uploader = new qq.FileUploaderBasic({
        action: [this.baseURL, this.attachmentPath].join(''),
        params: {
          reactive_client_id: window.reactiveClientId?.(),
        },
        customHeaders,
        withCredentials: this.baseURL !== window.location.origin,
        onComplete: (
          id: string,
          fileName: string,
          responseJSON: {
            attachmentThumb: string;
            attachmentUrl: string;
          }
        ) => {
          this.$enclosingElement
            .find('.attachments__body')
            .append(responseJSON.attachmentThumb);
          resolve(responseJSON.attachmentUrl);
        },
        onError: (id: string, fileName: string, errorReason: string) => {
          const message = `Upload failed: ${errorReason}`;
          showAlertMessage(message);
          reject(new Error(message));
        },
      });
      if (file) {
        uploader.uploadFileList([file]);
      }
    });
  }

  // Modal media chooser
  chooseMedia({ onMediaChosen, onChooserDismissed }) {
    if (!this.canAddAttachments) return;

    if (!this.$enclosingElement) {
      throw new Error('Could not get enclosing element');
    }

    const $attachmentWindow = $('#attachment-modal-window');
    const $chooserTemplate = this.$enclosingElement.find(
      '.form-modal-media-upload'
    );
    const $chooser = $($chooserTemplate.html());

    // We are not always in the gallery, sometimes this is a standalone modal.
    const $gallery = $attachmentWindow.find('.attachment-gallery');

    if ($gallery.length > 0) {
      $gallery.addClass('hide');
      $attachmentWindow.append($chooser);
    } else {
      // This html is taken from the DOM.
      // @ts-ignore
      // eslint-disable-next-line no-unsanitized/method
      $attachmentWindow.html($chooser);
    }

    let onClose;

    if ($gallery.length > 0) {
      onClose = () => {
        $attachmentWindow.css({ zIndex: '' });
        $('.modal-backdrop').last().css({ zIndex: '' });
        $chooser.remove();
        $gallery.removeClass('hide');
        this.attachmentsModal = null;

        if (onChooserDismissed) {
          onChooserDismissed();
        }
      };
    } else {
      onClose = () => {
        // @ts-ignore
        return $attachmentWindow.modal('hide');
      };
    }

    // @ts-ignore
    this.attachmentsModal = new window.CallbackAttachmentsModal({
      // In idea portals there is no attachment modal, so we pass the enclosing element and let
      // idea_portas/attachment.js figure out what to do.
      $attachmentWindow: $attachmentWindow.length
        ? $attachmentWindow
        : this.$enclosingElement,
      uploadUrl: this.attachmentPath,
      callback: (
        attachment: AttachmentsModalAttachment,
        response: AttachmentsModalCallbackResponse
      ) => {
        let url: string | undefined;
        let fileExt: string | undefined;
        let mimeType: string | undefined;
        let name: string | undefined;

        if (response) {
          url = response.attachmentUrl;
          fileExt = response.attachmentExt;
          name = response.attachmentTitle;
          this.$enclosingElement
            .find('.attachments__body')
            .append(response.attachmentThumb);
        } else {
          url = attachment.file.url;
          fileExt = attachment.file.ext;
          mimeType = attachment.file.type?.url;
          name = attachment.file.name;
        }

        onMediaChosen({
          url,
          fileExt,
          mimeType,
          name,
        });
      },
      $chooser,
      onClose,
      acceptFiles: null,
      allowedExtensions: [],
    });
    if ($attachmentWindow.length) {
      bringModalToFront('#attachment-modal-window', {
        onModalDismissed: () => {
          $chooser.remove();
          this.attachmentsModal = null;

          // Already called in onClose if gallery is present
          if ($gallery.length === 0) {
            onChooserDismissed();
          }
        },
      });

      // @ts-ignore
      $attachmentWindow.modal('show').addClass('modal-big');
    }
  }
}

export default AhaMediaProvider;
